import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Code, Eye, ArrowLeft, Save } from "lucide-react";

function slugify(t) {
  return (t || "").toString().toLowerCase().trim()
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s").replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "", tags: [], author: "TakipciSatis",
  status: "draft", seo_title: "", seo_description: "", seo_keywords: "", canonical: "", robots: "index,follow",
  og_title: "", og_description: "", og_image: "",
};

export default function BlogAdmin() {
  const [view, setView] = useState("list");
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [post, setPost] = useState(EMPTY);
  const [mode, setMode] = useState("visual");
  const [tagInput, setTagInput] = useState("");
  const [newCat, setNewCat] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const load = () => api.get("/admin/blogs").then((r) => setPosts(r.data)).catch(() => {});
  const loadCats = () => api.get("/admin/blog-categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); loadCats(); }, []);

  const openNew = () => { setPost(EMPTY); setSlugTouched(false); setView("editor"); };
  const openEdit = async (p) => { const { data } = await api.get(`/admin/blogs/${p.id}`); setPost({ ...EMPTY, ...data }); setSlugTouched(true); setView("editor"); };

  const setTitle = (t) => setPost((p) => ({ ...p, title: t, slug: slugTouched ? p.slug : slugify(t) }));

  const save = async (publish) => {
    if (!post.title) return toast.error("Başlık gerekli");
    const payload = { ...post, status: publish ? "published" : post.status };
    try {
      if (post.id) await api.patch(`/admin/blogs/${post.id}`, payload);
      else await api.post("/admin/blogs", payload);
      toast.success("Kaydedildi"); setView("list"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const del = async (p) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/admin/blogs/${p.id}`); toast.success("Silindi"); load(); };
  const addTag = () => { if (tagInput && !post.tags.includes(tagInput)) { setPost({ ...post, tags: [...post.tags, tagInput] }); setTagInput(""); } };
  const addCat = async () => { if (!newCat) return; await api.post("/admin/blog-categories", { name: newCat }); setNewCat(""); loadCats(); toast.success("Kategori eklendi"); };

  if (view === "list") {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-extrabold text-white">Blog Yönetimi</h1>
          <button onClick={openNew} className="btn-gradient px-5 py-2.5 rounded-xl font-bold flex items-center gap-2" data-testid="new-blog-btn"><Plus size={16} /> Yeni Yazı</button>
        </div>
        <div className="card-surface p-4 mb-4 flex gap-2 flex-wrap items-center">
          <span className="text-sm text-gray-400">Kategoriler:</span>
          {cats.map((c) => <span key={c.id} className="text-xs bg-white/5 px-3 py-1 rounded-full text-gray-300">{c.name}</span>)}
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Yeni kategori" className="bg-[#171922] border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs" data-testid="new-cat-input" />
          <button onClick={addCat} className="text-xs bg-white/10 px-3 py-1.5 rounded-lg text-white">Ekle</button>
        </div>
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-gray-400 text-left border-b border-white/10">{["Başlık", "Kategori", "Durum", "Görüntülenme", "Tarih", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`blog-row-${p.slug}`}>
                  <td className="px-4 py-3 text-white max-w-[280px] truncate">{p.title}</td>
                  <td className="px-4 py-3 text-gray-400">{p.category || "-"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${p.status === "published" ? "bg-green-500/15 text-green-400" : "bg-yellow-500/15 text-yellow-400"}`}>{p.status === "published" ? "Yayında" : "Taslak"}</span></td>
                  <td className="px-4 py-3 text-gray-400">{p.views || 0}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><div className="flex gap-1">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg bg-white/5 text-gray-300" data-testid={`edit-blog-${p.slug}`}><Edit size={15} /></button>
                    <button onClick={() => del(p)} className="p-1.5 rounded-lg bg-red-500/15 text-red-400"><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
              {posts.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Henüz yazı yok</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const field = (label, key, type = "text") => (
    <div><label className="text-xs text-gray-400">{label}</label>
      <input type={type} value={post[key] || ""} onChange={(e) => setPost({ ...post, [key]: e.target.value })} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <button onClick={() => setView("list")} className="flex items-center gap-2 text-gray-400 hover:text-white"><ArrowLeft size={18} /> Geri</button>
        <div className="flex gap-2">
          <button onClick={() => save(false)} className="px-5 py-2.5 rounded-xl bg-white/10 text-white font-semibold flex items-center gap-2" data-testid="save-draft-btn"><Save size={16} /> Taslak Kaydet</button>
          <button onClick={() => save(true)} className="btn-gradient px-5 py-2.5 rounded-xl font-bold" data-testid="publish-blog-btn">Yayınla</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="card-surface p-5 space-y-4">
            <div><label className="text-xs text-gray-400">Başlık</label><input value={post.title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-3 text-white mt-1 text-lg font-semibold" data-testid="blog-title-input" /></div>
            <div><label className="text-xs text-gray-400">Slug (URL)</label><input value={post.slug} onChange={(e) => { setSlugTouched(true); setPost({ ...post, slug: slugify(e.target.value) }); }} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1 font-mono" data-testid="blog-slug-input" /><p className="text-xs text-gray-500 mt-1">/blog/{post.slug}</p></div>
            <div><label className="text-xs text-gray-400">Kısa Açıklama</label><textarea value={post.excerpt} onChange={(e) => setPost({ ...post, excerpt: e.target.value })} rows={2} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
          </div>

          <div className="card-surface p-5">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-white">İçerik</label>
              <div className="flex gap-1 bg-[#171922] rounded-lg p-1">
                <button onClick={() => setMode("visual")} className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 ${mode === "visual" ? "bg-white text-[#1f222d]" : "text-gray-400"}`} data-testid="mode-visual"><Eye size={13} /> Görsel</button>
                <button onClick={() => setMode("html")} className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 ${mode === "html" ? "bg-white text-[#1f222d]" : "text-gray-400"}`} data-testid="mode-html"><Code size={13} /> HTML</button>
              </div>
            </div>
            {mode === "html" ? (
              <textarea value={post.content} onChange={(e) => setPost({ ...post, content: e.target.value })} rows={18}
                className="w-full bg-[#0f1117] border border-white/10 rounded-xl px-4 py-3 text-green-300 text-sm font-mono leading-relaxed" spellCheck={false}
                placeholder="<h2>Başlık</h2>&#10;<p>İçerik...</p>" data-testid="blog-html-editor" />
            ) : (
              <div contentEditable suppressContentEditableWarning onInput={(e) => setPost({ ...post, content: e.currentTarget.innerHTML })}
                dangerouslySetInnerHTML={{ __html: post.content }} data-testid="blog-visual-editor"
                className="blog-content bg-[#0f1117] border border-white/10 rounded-xl px-4 py-3 min-h-[380px] outline-none focus:border-[#d8b4fe]" />
            )}
            <p className="text-xs text-gray-500 mt-2">HTML modunda h2, p, ul, li, a, img, blockquote, table gibi etiketleri doğrudan yazabilirsiniz. Kaydedilirken güvenlik için sanitize edilir (XSS koruması).</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-surface p-5 space-y-3">
            <h3 className="font-semibold text-white text-sm">Yayın</h3>
            {field("Kapak Görseli (URL)", "cover_image")}
            <div><label className="text-xs text-gray-400">Kategori</label>
              <select value={post.category} onChange={(e) => setPost({ ...post, category: e.target.value })} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1">
                <option value="">Seçiniz</option>{cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            {field("Yazar", "author")}
            <div><label className="text-xs text-gray-400">Etiketler</label>
              <div className="flex gap-2 mt-1"><input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} className="flex-1 bg-[#171922] border border-white/10 rounded-xl px-3 py-2 text-white text-sm" /><button onClick={addTag} className="px-3 bg-white/10 rounded-xl text-white text-sm">+</button></div>
              <div className="flex flex-wrap gap-1 mt-2">{post.tags.map((t) => <span key={t} onClick={() => setPost({ ...post, tags: post.tags.filter((x) => x !== t) })} className="text-xs bg-white/5 px-2 py-1 rounded-full text-gray-300 cursor-pointer hover:bg-red-500/15 hover:text-red-400">#{t} ×</span>)}</div>
            </div>
          </div>
          <div className="card-surface p-5 space-y-3">
            <h3 className="font-semibold text-white text-sm">SEO</h3>
            {field("SEO Başlık", "seo_title")}
            <div><label className="text-xs text-gray-400">Meta Açıklama</label><textarea value={post.seo_description} onChange={(e) => setPost({ ...post, seo_description: e.target.value })} rows={2} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
            {field("Anahtar Kelimeler", "seo_keywords")}
            {field("Canonical URL", "canonical")}
            {field("Robots", "robots")}
            {field("OG Görsel", "og_image")}
          </div>
        </div>
      </div>
    </div>
  );
}
